import { invalidateMinicart } from "~/app/store/bffApi";
import { B2CCartProcessTile } from "~/features/cart/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { Box, Button, styled } from "~/common/mui";
import { useShowAffiliateMainBanner } from "~/domains/affiliate/utils/utils";
import InformationContainer from "~/features/cart/B2CRequestPage/components/InformationContainer";
import { useDispatch, useSelector } from "react-redux";
import { useOnMount } from "~/common/hooks/useOnMount";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { type RootState } from "~/app/store";
import { type CartElement } from "~/features/cart/B2CRequestPage/components/B2CRequestCart/CartElement";
import { B2CRequestItemsSection } from "~/features/cart/B2CRequestPage/components/B2CRequestCart/B2CRequestItemsSection";

import { useMemo, useRef, useState } from "react";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { requestCart } from "~/features/cart/B2CRequestPage/api/requestCart";
import { type SalesPartnerInfo, type UserInfoData } from "~/features/cart/B2CRequestPage/types";
import { t } from "i18next";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { transformSalesPartnerData } from "~/features/cart/B2CRequestPage/utils";
import { format } from "~/common/utils/ApiHelper";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import { useCartPnaQueries } from "~/features/cart/cart/hooks/useCartPnaQueries";
import { FormProvider, useForm } from "react-hook-form";
import { InquirySuccessPage } from "~/features/inquirySuccess/components/InquirySuccessPage";
import { createInquirySuccessUserData } from "~/features/inquirySuccess/utils/createInquirySuccessUserData";
import { userIsLoggedIn } from "~/domains/user/utils/roles";

const TABLE_HEADER_BORDER = 3;

const Footer = styled("div")(({ theme }) => ({
  display: "flex",
  paddingTop: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: theme.spacing(4),
    direction: "rtl",
    borderTop: `${TABLE_HEADER_BORDER}px solid ${theme.palette.divider}`,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: "repeat(4, 1fr)",
  },
}));

const REQUIRED_INFO_FIELDS: (keyof UserInfoData)[] = ["company", "salutationCode", "firstName", "lastName", "country"];

export interface B2CRequestPageProps {
  userInfo: UserInfoData;
  salesPartner: SalesPartnerInfo;
  marketingConsent: boolean;
  cartId?: string;
  guestCheckoutContext?: string;
}

export interface DataObject {
  requestNumber: string;
  userDetails: UserInfoData;
  salesPartnerData: SalesPartnerInfo;
}

interface CheckboxesForm {
  userAgreement: boolean;
  tcAgreement: boolean;
  ppAgreement: boolean;
}

//copied from B2CCart — raw cart/product data only; descriptors project from the query-backed
// cart PnA hook (A07) instead of the legacy state.products dictionaries.
const selectElementsData = (root: RootState) => {
  const cart = root.cart;
  const products = root.products;
  const lineItems = Object.values(cart.packageEntriesById);

  return { lineItems, packEntries: cart.packageEntriesById, products };
};

const B2CRequestPage = ({
  userInfo,
  salesPartner,
  marketingConsent,
  cartId,
  guestCheckoutContext,
}: B2CRequestPageProps) => {
  const initialValues: DataObject = (() => {
    const userBase: UserInfoData = {
      company: userInfo.company,
      titleCode: userInfo.titleCode,
      salutationCode: userInfo.salutationCode,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      line1: userInfo.line1, // Line 1 contains house number and street
      additionalLine1: userInfo.additionalLine1, // Additional address line (displayed after Line1)
      line2: userInfo.line2, // Line2 contains the city, region and postalcode
      zipCode: userInfo.zipCode,
      city: userInfo.city,
      country: userInfo.country,
      phone: userInfo.phone,
      email: userInfo.email,
      updateUser: true,
    };

    return {
      requestNumber: "",
      userDetails: userBase,
      salesPartnerData: salesPartner,
    };
  })();

  const infoRef = useRef<HTMLDivElement>(null);

  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const isAffiliate = useShowAffiliateMainBanner();
  const formProps = useForm<CheckboxesForm>({
    defaultValues: {
      userAgreement: marketingConsent,
      tcAgreement: false,
      ppAgreement: false,
    },
  });

  const { val: showSuccessScreen, setTrue: displaySuccessScreen } = useBooleanState(false);

  const dispatch = useDispatch();

  const [dataObject, setDataObject] = useState<DataObject>(initialValues);
  const [guestContext, setGuestContext] = useState(guestCheckoutContext);

  useOnMount(() => {
    dispatch(cartPageLoadThunk());
  });

  const hasAllUserInfo = REQUIRED_INFO_FIELDS.every((prop) => {
    if (dataObject.userDetails[prop] === "") {
      return false;
    }
    return Object.prototype.hasOwnProperty.call(dataObject.userDetails, prop);
  });

  const elementsData = useSelector(selectElementsData);
  const { descriptorFor } = useCartPnaQueries();

  const cartElements = useMemo<CartElement[]>(
    () =>
      elementsData.lineItems.map((lineItem) => ({
        data: lineItem,
        product: elementsData.products.basic[lineItem.productCode],
        descriptor: descriptorFor(lineItem.productCode, lineItem.priceReference),
      })),
    [elementsData, descriptorFor],
  );

  const onButtonClick = formProps.handleSubmit((data) => {
    const { userAgreement, tcAgreement, ppAgreement } = data;
    requestCart({
      requestNumber: dataObject.requestNumber,
      tcConsent: tcAgreement,
      ppConsent: ppAgreement,
      marketingConsent: userAgreement,
      cartId,
      guestCheckoutContext: guestContext,
    }).then((response) => {
      const id = format(new Date(), "YYYYDDDDHHmmssSSS");
      dispatch(invalidateMinicart());

      AnalyticsMQ.instance.push("placeOrder", {
        orderId: id,
        type: "quote",
        paymentType: "Sales Partner Order",
        purchaseValueEur: response.analytics?.purchaseValueEur,
        entries: cartElements.map((item) => ({
          lineItem: item.data,
          product: item.descriptor,
        })),
        salesPartner: transformSalesPartnerData(dataObject.salesPartnerData),
      });

      displaySuccessScreen();
    });
  });

  let inquirySuccessUserData = undefined;
  if (!userIsLoggedIn()) {
    inquirySuccessUserData = createInquirySuccessUserData({
      userInfo: dataObject.userDetails,
      salesPartner: dataObject.salesPartnerData,
      marketingConsent: formProps.watch("userAgreement"),
      guestCheckoutContext: guestContext,
    });
  }

  return (
    <div>
      {!isAffiliate && !showSuccessScreen && <B2CCartProcessTile activeIndex={2} />}
      <Box sx={{ mt: !showSuccessScreen ? 4 : 0 }}>
        {(!showSuccessScreen || isTablet) && <PageTitle>{t("page.cart.b2c.process.step.3")}</PageTitle>}
      </Box>

      {!showSuccessScreen && (
        <>
          <FormProvider {...formProps}>
            <InformationContainer
              ref={infoRef}
              onButtonClick={() => {
                onButtonClick();
              }}
              marketingConsent={marketingConsent}
              dataObject={dataObject}
              setDataObject={setDataObject}
              className="mb-30"
              hasAllUserInfo={hasAllUserInfo}
              cartId={cartId}
              guestCheckoutContext={guestContext}
              onGuestCheckoutContextChange={setGuestContext}
            />
          </FormProvider>

          <B2CRequestItemsSection items={cartElements} />
          <Footer>
            <Button
              onClick={() => {
                onButtonClick();
              }}
              fullWidth
              disabled={!hasAllUserInfo}
            >
              {t("page.cart.b2c.process.step.3")}
            </Button>
          </Footer>
        </>
      )}
      {showSuccessScreen && <InquirySuccessPage userData={inquirySuccessUserData} />}
    </div>
  );
};

export default B2CRequestPage;

export function createB2CRequestPage(data: B2CRequestPageProps) {
  return <B2CRequestPage {...data} />;
}
